import { arrayHelpers } from '@approvalmax/utils';
import { constants, selectors } from 'modules/common';
import { domain, State } from 'modules/data';
import { useShallowEqualSelector } from 'modules/react-redux';
import {
    qbooksEmailToCustomerChangeData,
    qbooksEmailToCustomerInit,
    qbooksEmailToCustomerToggle,
    removeEmailToContactAttachment,
} from 'pages/requestForm/actions';
import EmailToSupplierForm from 'pages/requestForm/components/EmailToSupplierForm/EmailToSupplierForm';
import {
    EmailToSupplierChangeValidator,
    EmailToSupplierContactPersonData,
    EmailToSupplierDataChangeHandler,
} from 'pages/requestForm/components/EmailToSupplierForm/EmailToSupplierForm.types';
import {
    getNewSupplierAttachments,
    getShowValidationErrors,
    isLoading,
} from 'pages/requestForm/selectors/pageSelectors';
import { FC, memo, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { getContacts } from '../../../QBooksEmailToVendorSection/QBooksEmailToVendorSection.helpers';
import { labels, placeholderList } from './QBooksEmailToCustomerSection.constants';
import { QBooksEmailToCustomerSectionProps } from './QBooksEmailToCustomerSection.types';

export const QBooksEmailToCustomerSection: FC<QBooksEmailToCustomerSectionProps> = memo((props) => {
    const { request, isReadonly } = props;

    const dispatch = useDispatch();

    const { me, showErrors, newAttachments, loading } = useShallowEqualSelector((state: State) => {
        return {
            me: selectors.profile.getProfileUser(state),
            showErrors: getShowValidationErrors(state),
            newAttachments: getNewSupplierAttachments(state),
            loading: isLoading(state),
        };
    });

    const {
        details: { customer, emailToContact },
    } = request;

    useEffect(() => {
        dispatch(qbooksEmailToCustomerInit());
    }, [dispatch]);

    const contactPersons: domain.QBooksContactPerson[] = customer?.contactPersons ?? arrayHelpers.emptyArray();

    const changeData = useCallback<EmailToSupplierDataChangeHandler>(
        (value, key) => {
            if (emailToContact) {
                dispatch(qbooksEmailToCustomerChangeData(value, key));
            }
        },
        [dispatch, emailToContact]
    );

    const validateTo = useCallback<EmailToSupplierChangeValidator>((data) => {
        return data.to.length === 0 || data.to.join(',').length > constants.qBooksConstants.CUSTOMER_EMAIL_MAX_LENGTH;
    }, []);

    const validateCc = useCallback<EmailToSupplierChangeValidator>((data) => {
        return data.cc.join(',').length > constants.qBooksConstants.CUSTOMER_EMAIL_CC_MAX_LENGTH;
    }, []);

    const filterContactPersonItem = useCallback((contact: EmailToSupplierContactPersonData, filterText: string) => {
        return (
            contact.text.toUpperCase().includes(filterText) || contact.emailAddress.toUpperCase().includes(filterText)
        );
    }, []);

    const onEmailToCustomerToggle = useCallback(
        (value: boolean) => {
            dispatch(qbooksEmailToCustomerToggle(value));
        },
        [dispatch]
    );

    const onAttachmentRemove = useCallback(
        (attachmentId: string) => {
            dispatch(removeEmailToContactAttachment(attachmentId));
        },
        [dispatch]
    );

    return (
        <EmailToSupplierForm
            labels={labels}
            data={emailToContact}
            onChangeData={changeData}
            contacts={getContacts(contactPersons, me)}
            toOptionsFilterFunction={filterContactPersonItem}
            toValidatorFunction={validateTo}
            ccOptionsFilterFunction={filterContactPersonItem}
            ccValidatorFunction={validateCc}
            isVisible={Boolean(emailToContact?.emailHasToBeSent)}
            onVisibilityToggle={onEmailToCustomerToggle}
            attachments={emailToContact?.attachments || []}
            attachmentsNew={newAttachments}
            isAttachmentsLoading={loading}
            onAttachRemove={onAttachmentRemove}
            hasErrors={showErrors}
            integrationType={domain.IntegrationType.QBooks}
            isReadonly={isReadonly}
            placeholderList={placeholderList}
        />
    );
});

QBooksEmailToCustomerSection.displayName = 'QBooksEmailToCustomerSection';
